import { useState, useRef, useCallback, useEffect } from "react";
import type { SketchFeature, SketchCommand } from "./featureTypes";

type SketchTool = "select" | "line" | "arc" | "bezier" | "spline";

interface SketchEditorProps {
  feature: SketchFeature;
  onUpdateCommands: (commands: SketchCommand[], closed: boolean) => void;
  onExit: () => void;
}

const GRID_SIZES = [0.5, 1, 2, 5, 10];

function snapToGrid(p: [number, number], gridSize: number): [number, number] {
  return [
    Math.round(p[0] / gridSize) * gridSize,
    Math.round(p[1] / gridSize) * gridSize,
  ];
}

/** Get the last endpoint from command list */
function lastPoint(commands: SketchCommand[]): [number, number] | null {
  if (commands.length === 0) return null;
  return commands[commands.length - 1].point;
}

/** Calculate sagitta from cursor position projected onto chord perpendicular bisector */
function computeSagitta(chordStart: [number, number], chordEnd: [number, number], cursorPos: [number, number]): number {
  const dx = chordEnd[0] - chordStart[0];
  const dy = chordEnd[1] - chordStart[1];
  const chord = Math.sqrt(dx * dx + dy * dy);
  if (chord === 0) return 0;
  const mx = (chordStart[0] + chordEnd[0]) / 2;
  const my = (chordStart[1] + chordEnd[1]) / 2;
  const nx = -dy / chord;
  const ny = dx / chord;
  return (cursorPos[0] - mx) * nx + (cursorPos[1] - my) * ny;
}

/** Convert SVG arc params from sagitta to SVG A command */
function sagittaArcPath(start: [number, number], end: [number, number], sagitta: number): string {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const chord = Math.sqrt(dx * dx + dy * dy);
  if (chord === 0 || sagitta === 0) return `L ${end[0]} ${end[1]}`;
  const radius = (chord * chord) / (8 * Math.abs(sagitta)) + Math.abs(sagitta) / 2;
  const sweep = sagitta > 0 ? 1 : 0;
  const largeArc = Math.abs(sagitta) > chord / 2 ? 1 : 0;
  return `A ${radius} ${radius} 0 ${largeArc} ${sweep} ${end[0]} ${end[1]}`;
}

/** Build SVG path data from sketch commands */
function commandsToSvgPath(commands: SketchCommand[], closed: boolean): string {
  const parts: string[] = [];
  for (let i = 0; i < commands.length; i++) {
    const cmd = commands[i];
    if (cmd.tool === "moveTo") {
      parts.push(`M ${cmd.point[0]} ${cmd.point[1]}`);
    } else if (cmd.tool === "lineTo") {
      parts.push(`L ${cmd.point[0]} ${cmd.point[1]}`);
    } else if (cmd.tool === "arcTo") {
      const prev = i > 0 ? commands[i - 1].point : null;
      if (prev) {
        parts.push(sagittaArcPath(prev, cmd.point, cmd.sagitta));
      } else {
        parts.push(`L ${cmd.point[0]} ${cmd.point[1]}`);
      }
    } else if (cmd.tool === "bezierTo") {
      parts.push(`C ${cmd.cp1[0]} ${cmd.cp1[1]} ${cmd.cp2[0]} ${cmd.cp2[1]} ${cmd.point[0]} ${cmd.point[1]}`);
    } else if (cmd.tool === "splineTo") {
      // Approximate spline as a line in SVG preview — replicad computes the actual curve
      parts.push(`L ${cmd.point[0]} ${cmd.point[1]}`);
    }
  }
  if (closed && commands.length > 1) parts.push("Z");
  return parts.join(" ");
}

export default function SketchEditor({ feature, onUpdateCommands, onExit }: SketchEditorProps) {
  const [activeTool, setActiveTool] = useState<SketchTool>("line");
  const [gridSize, setGridSize] = useState(1);
  const [cursor, setCursor] = useState<[number, number]>([0, 0]);
  const [viewBox, setViewBox] = useState({ x: -30, y: -30, w: 60, h: 60 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{ x: number; y: number; vx: number; vy: number } | null>(null);
  // Arc tool: first click sets endpoint, second sets sagitta
  const [arcEndpoint, setArcEndpoint] = useState<[number, number] | null>(null);
  // Bezier tool: accumulate endpoint, cp1, cp2 (3 clicks)
  const [bezierClicks, setBezierClicks] = useState<[number, number][]>([]);
  // Select tool: dragging state
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [undoStack, setUndoStack] = useState<SketchCommand[][]>([]);

  const svgRef = useRef<SVGSVGElement>(null);
  const commands = feature.commands;
  const closed = feature.closed;

  const setTool = useCallback((tool: SketchTool) => {
    setActiveTool(tool);
    setArcEndpoint(null);
    setBezierClicks([]);
  }, []);

  const handleUndo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      onUpdateCommands(prev[prev.length - 1], closed);
      return prev.slice(0, -1);
    });
  }, [closed, onUpdateCommands]);

  // Convert mouse event to SVG coordinates
  const svgCoords = useCallback((e: React.MouseEvent): [number, number] => {
    const svg = svgRef.current;
    if (!svg) return [0, 0];
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return [0, 0];
    const p = pt.matrixTransform(ctm.inverse());
    return [p.x, p.y];
  }, []);

  // Snap to existing point if close enough
  const snapThreshold = viewBox.w / 30;
  const snapPoint = useCallback((p: [number, number]): [number, number] => {
    for (const cmd of commands) {
      const dx = cmd.point[0] - p[0];
      const dy = cmd.point[1] - p[1];
      if (Math.sqrt(dx * dx + dy * dy) < snapThreshold) {
        return cmd.point;
      }
    }
    return snapToGrid(p, gridSize);
  }, [commands, gridSize, snapThreshold]);

  // Mouse move — update cursor + handle drag
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const raw = svgCoords(e);

    // Point dragging
    if (dragIdx !== null) {
      const snapped = snapPoint(raw);
      const updated = [...commands];
      updated[dragIdx] = { ...updated[dragIdx], point: snapped } as SketchCommand;
      onUpdateCommands(updated, closed);
      setCursor(snapped);
      return;
    }

    setCursor(snapPoint(raw));

    // Pan handling
    if (isPanning && panStart) {
      const dx = (e.clientX - panStart.x) / (svgRef.current?.clientWidth ?? 1) * viewBox.w;
      const dy = (e.clientY - panStart.y) / (svgRef.current?.clientHeight ?? 1) * viewBox.h;
      setViewBox({ ...viewBox, x: panStart.vx - dx, y: panStart.vy - dy });
    }
  }, [svgCoords, snapPoint, isPanning, panStart, viewBox, dragIdx, commands, closed, onUpdateCommands]);

  // Mouse down — pan or start point drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY, vx: viewBox.x, vy: viewBox.y });
      return;
    }
    // Select tool: start dragging if near a point
    if (activeTool === "select" && e.button === 0) {
      const p = svgCoords(e);
      for (let i = 0; i < commands.length; i++) {
        const dx = commands[i].point[0] - p[0];
        const dy = commands[i].point[1] - p[1];
        if (Math.sqrt(dx * dx + dy * dy) < snapThreshold) {
          setUndoStack((prev) => [...prev, [...commands]]);
          setDragIdx(i);
          setSelectedIdx(i);
          return;
        }
      }
      setSelectedIdx(null);
    }
  }, [viewBox, activeTool, svgCoords, commands, snapThreshold]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    setPanStart(null);
    setDragIdx(null);
  }, []);

  // Zoom with mouse wheel
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const raw = svgCoords(e);
    const factor = e.deltaY > 0 ? 1.15 : 0.87;
    const newW = viewBox.w * factor;
    const newH = viewBox.h * factor;
    // Zoom centered on cursor
    const ratioX = (raw[0] - viewBox.x) / viewBox.w;
    const ratioY = (raw[1] - viewBox.y) / viewBox.h;
    setViewBox({
      x: raw[0] - ratioX * newW,
      y: raw[1] - ratioY * newH,
      w: newW,
      h: newH,
    });
  }, [svgCoords, viewBox]);

  // Click — add command based on active tool
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (isPanning || e.altKey || e.button !== 0 || dragIdx !== null) return;
    if (activeTool === "select") return; // select uses mousedown/drag
    const p = snapPoint(svgCoords(e));

    if (activeTool === "line") {
      setUndoStack((prev) => [...prev, [...commands]]);
      if (commands.length === 0) {
        onUpdateCommands([{ tool: "moveTo", point: p }], closed);
      } else {
        onUpdateCommands([...commands, { tool: "lineTo", point: p }], closed);
      }
    } else if (activeTool === "arc") {
      if (!arcEndpoint) {
        setArcEndpoint(p);
      } else {
        const last = lastPoint(commands);
        if (last) {
          const sagitta = computeSagitta(last, arcEndpoint, p);
          if (sagitta !== 0) {
            setUndoStack((prev) => [...prev, [...commands]]);
            onUpdateCommands([...commands, { tool: "arcTo", point: arcEndpoint, sagitta: Math.round(sagitta * 10) / 10 || 0.1 }], closed);
          }
        }
        setArcEndpoint(null);
      }
    } else if (activeTool === "bezier") {
      const clicks = [...bezierClicks, p];
      if (clicks.length === 1) {
        // First click: endpoint
        setBezierClicks(clicks);
      } else if (clicks.length === 2) {
        // Second click: control point 1
        setBezierClicks(clicks);
      } else {
        // Third click: control point 2 → create bezier command
        setUndoStack((prev) => [...prev, [...commands]]);
        onUpdateCommands([...commands, { tool: "bezierTo", point: clicks[0], cp1: clicks[1], cp2: clicks[2] }], closed);
        setBezierClicks([]);
      }
    } else if (activeTool === "spline") {
      setUndoStack((prev) => [...prev, [...commands]]);
      if (commands.length === 0) {
        onUpdateCommands([{ tool: "moveTo", point: p }], closed);
      } else {
        onUpdateCommands([...commands, { tool: "splineTo", point: p }], closed);
      }
    }
  }, [activeTool, commands, closed, svgCoords, isPanning, arcEndpoint, bezierClicks, dragIdx, snapPoint, onUpdateCommands]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
      if (e.key === "l" || e.key === "L") setTool("line");
      else if (e.key === "a" || e.key === "A") setTool("arc");
      else if (e.key === "b" || e.key === "B") setTool("bezier");
      else if (e.key === "p" || e.key === "P") setTool("spline");
      else if (e.key === "s" || e.key === "S") setTool("select");
      else if (e.key === "Escape") { onExit(); }
      else if ((e.key === "Delete" || e.key === "Backspace") && selectedIdx !== null) {
        e.preventDefault();
        setUndoStack((prev) => [...prev, [...commands]]);
        onUpdateCommands(commands.filter((_, i) => i !== selectedIdx), closed);
        setSelectedIdx(null);
      }
      else if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleUndo, onExit, setTool, selectedIdx, commands, closed, onUpdateCommands]);

  const handleClose = useCallback(() => {
    onUpdateCommands(commands, true);
  }, [commands, onUpdateCommands]);

  const handleOpen = useCallback(() => {
    onUpdateCommands(commands, false);
  }, [commands, onUpdateCommands]);

  // Compute grid lines
  const gridLines: JSX.Element[] = [];
  const gx0 = Math.floor(viewBox.x / gridSize) * gridSize;
  const gy0 = Math.floor(viewBox.y / gridSize) * gridSize;
  for (let x = gx0; x < viewBox.x + viewBox.w; x += gridSize) {
    const major = Math.abs(x) < 0.001 || Math.abs(x % (gridSize * 5)) < 0.001;
    gridLines.push(
      <line key={`gx${x}`} x1={x} y1={viewBox.y} x2={x} y2={viewBox.y + viewBox.h}
        className={major ? "sketch-grid-major" : "sketch-grid-line"} />
    );
  }
  for (let y = gy0; y < viewBox.y + viewBox.h; y += gridSize) {
    const major = Math.abs(y) < 0.001 || Math.abs(y % (gridSize * 5)) < 0.001;
    gridLines.push(
      <line key={`gy${y}`} x1={viewBox.x} y1={y} x2={viewBox.x + viewBox.w} y2={y}
        className={major ? "sketch-grid-major" : "sketch-grid-line"} />
    );
  }

  const pathData = commandsToSvgPath(commands, closed);
  const last = lastPoint(commands);
  // Preview sagitta for arc tool (computed once, used in JSX)
  const previewSagitta = last && arcEndpoint ? computeSagitta(last, arcEndpoint, cursor) : 0;

  return (
    <div className="sketch-editor">
      {/* Toolbar */}
      <div className="sketch-toolbar">
        <button className={`sketch-tool-btn ${activeTool === "select" ? "active" : ""}`}
          onClick={() => setTool("select")} title="Select (S)">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 2l3 12 2-4 4-2L3 2z"/></svg>
        </button>
        <button className={`sketch-tool-btn ${activeTool === "line" ? "active" : ""}`}
          onClick={() => setTool("line")} title="Line (L)">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 13L13 3"/><circle cx="3" cy="13" r="1.5"/><circle cx="13" cy="3" r="1.5"/></svg>
        </button>
        <button className={`sketch-tool-btn ${activeTool === "arc" ? "active" : ""}`}
          onClick={() => setTool("arc")} title="Arc (A)">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 13 A 10 10 0 0 1 13 3"/><circle cx="3" cy="13" r="1.5"/><circle cx="13" cy="3" r="1.5"/></svg>
        </button>
        <button className={`sketch-tool-btn ${activeTool === "bezier" ? "active" : ""}`}
          onClick={() => setTool("bezier")} title="Bezier (B)">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 13 C 3 5 13 11 13 3"/><circle cx="3" cy="5" r="1" strokeDasharray="1 1"/><circle cx="13" cy="11" r="1" strokeDasharray="1 1"/></svg>
        </button>
        <button className={`sketch-tool-btn ${activeTool === "spline" ? "active" : ""}`}
          onClick={() => setTool("spline")} title="Spline (P)">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 13 Q 8 1 13 8"/></svg>
        </button>
        <div className="sketch-toolbar-sep" />
        <button className="sketch-tool-btn" onClick={closed ? handleOpen : handleClose}
          title={closed ? "Open sketch" : "Close sketch"}>
          {closed ? "Open" : "Close"}
        </button>
        <button className="sketch-tool-btn" onClick={handleUndo} title="Undo (Ctrl+Z)" disabled={undoStack.length === 0}>
          Undo
        </button>
        <div className="sketch-toolbar-sep" />
        <select className="sketch-grid-select" value={gridSize} onChange={(e) => setGridSize(Number(e.target.value))}>
          {GRID_SIZES.map((s) => <option key={s} value={s}>Grid {s}</option>)}
        </select>
        <div className="sketch-toolbar-spacer" />
        <button className="sketch-tool-btn sketch-exit-btn" onClick={onExit} title="Finish editing sketch (Esc)">
          Done
        </button>
      </div>

      {/* Hint bar */}
      <div className="sketch-hint-bar">
        {activeTool === "line" && (commands.length === 0 ? "Click to place first point" : "Click to add line segment")}
        {activeTool === "arc" && !arcEndpoint && "Click to set arc endpoint"}
        {activeTool === "arc" && arcEndpoint && "Click to set arc curvature"}
        {activeTool === "bezier" && bezierClicks.length === 0 && "Click to set bezier endpoint"}
        {activeTool === "bezier" && bezierClicks.length === 1 && "Click to set control point 1"}
        {activeTool === "bezier" && bezierClicks.length === 2 && "Click to set control point 2"}
        {activeTool === "spline" && (commands.length === 0 ? "Click to place first point" : "Click to add smooth spline point")}
        {activeTool === "select" && "Click points to select · Drag to move · Delete to remove"}
      </div>

      {/* SVG Canvas */}
      <div className="sketch-canvas-wrap">
        <svg
          ref={svgRef}
          className="sketch-canvas"
          viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={handleClick}
          onWheel={handleWheel}
          style={{ cursor: isPanning ? "grabbing" : activeTool === "select" ? "default" : "crosshair" }}
        >
          {/* Grid */}
          {gridLines}

          {/* Origin axes */}
          <line x1={viewBox.x} y1={0} x2={viewBox.x + viewBox.w} y2={0} className="sketch-axis-x" />
          <line x1={0} y1={viewBox.y} x2={0} y2={viewBox.y + viewBox.h} className="sketch-axis-y" />

          {/* Sketch path */}
          {pathData && <path d={pathData} className="sketch-path" />}

          {/* Control points */}
          {commands.map((cmd, i) => (
            <g key={i}>
              <circle cx={cmd.point[0]} cy={cmd.point[1]} r={viewBox.w / 120}
                className={`sketch-point ${i === 0 ? "sketch-point-start" : ""} ${i === selectedIdx ? "sketch-point-selected" : ""}`} />
              {/* Bezier control handles */}
              {cmd.tool === "bezierTo" && (
                <>
                  <line x1={commands[i - 1]?.point[0] ?? 0} y1={commands[i - 1]?.point[1] ?? 0}
                    x2={cmd.cp1[0]} y2={cmd.cp1[1]} className="sketch-handle-line" />
                  <circle cx={cmd.cp1[0]} cy={cmd.cp1[1]} r={viewBox.w / 160} className="sketch-handle-point" />
                  <line x1={cmd.point[0]} y1={cmd.point[1]} x2={cmd.cp2[0]} y2={cmd.cp2[1]} className="sketch-handle-line" />
                  <circle cx={cmd.cp2[0]} cy={cmd.cp2[1]} r={viewBox.w / 160} className="sketch-handle-point" />
                </>
              )}
            </g>
          ))}

          {/* Preview line from last point to cursor */}
          {last && activeTool === "line" && !isPanning && (
            <line x1={last[0]} y1={last[1]} x2={cursor[0]} y2={cursor[1]} className="sketch-preview" />
          )}

          {/* Arc preview */}
          {last && activeTool === "arc" && arcEndpoint && !isPanning && (
            <>
              {/* Show the arc endpoint */}
              <circle cx={arcEndpoint[0]} cy={arcEndpoint[1]} r={viewBox.w / 100} className="sketch-point-pending" />
              {/* Preview arc from last point through cursor curvature to endpoint */}
              <path d={`M ${last[0]} ${last[1]} ${sagittaArcPath(last, arcEndpoint, previewSagitta)}`} className="sketch-preview" />
            </>
          )}

          {/* Arc endpoint preview (first click) */}
          {last && activeTool === "arc" && !arcEndpoint && !isPanning && (
            <line x1={last[0]} y1={last[1]} x2={cursor[0]} y2={cursor[1]} className="sketch-preview" />
          )}

          {/* Bezier preview */}
          {last && activeTool === "bezier" && !isPanning && bezierClicks.length === 0 && (
            <line x1={last[0]} y1={last[1]} x2={cursor[0]} y2={cursor[1]} className="sketch-preview" />
          )}
          {last && activeTool === "bezier" && !isPanning && bezierClicks.length >= 1 && (
            <circle cx={bezierClicks[0][0]} cy={bezierClicks[0][1]} r={viewBox.w / 100} className="sketch-point-pending" />
          )}
          {last && activeTool === "bezier" && !isPanning && bezierClicks.length >= 2 && (
            <circle cx={bezierClicks[1][0]} cy={bezierClicks[1][1]} r={viewBox.w / 120} className="sketch-handle-point" />
          )}

          {/* Spline preview */}
          {last && activeTool === "spline" && !isPanning && (
            <line x1={last[0]} y1={last[1]} x2={cursor[0]} y2={cursor[1]} className="sketch-preview" />
          )}

          {/* Snap cursor dot */}
          {!isPanning && activeTool !== "select" && (
            <circle cx={cursor[0]} cy={cursor[1]} r={viewBox.w / 150} className="sketch-cursor" />
          )}
        </svg>
      </div>

      {/* Coordinate bar */}
      <div className="sketch-coord-bar">
        <span>X: {cursor[0].toFixed(1)}</span>
        <span>Y: {cursor[1].toFixed(1)}</span>
        <span>{commands.length} pts</span>
        <span>{closed ? "Closed" : "Open"}</span>
      </div>
    </div>
  );
}
